import { useMaskito } from "@maskito/react";
import {
  forwardRef,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMergeRefs } from "@floating-ui/react";
import { YearlessDateMode, YearlessDate } from "../types";
import { usePrevious } from "../../../internal/hooks";
import yearlessDatePlaceholderMask from "./maskito/yearlessDatePlaceholderMask";
import { swapMode, stringifyYearlessDate, parseInputValue } from "./utils";
import textFieldStyles from "../../TextField/TextField.module.scss";
import dateFieldYearlessStyles from "../DateFieldYearless.module.scss";
import cx from "classnames";
import { PassThroughPropsWithRef, Size } from "../../../types";
import { Spinner } from "../../Spinner";

export type YearlessDateInputChange = {
  event: React.ChangeEvent<HTMLInputElement> | null;
  value: YearlessDate | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
};

export type YearlessDateInputChangeHandler = (
  change: YearlessDateInputChange,
) => void;

export type YearlessDateInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "ref" | "size"
> & {
  onChange: YearlessDateInputChangeHandler;
  mode?: YearlessDateMode;
  value?: YearlessDate | null;
  error?: boolean;
  size?: Extract<Size, "small" | "medium" | "large">;
  wrapperProps?: PassThroughPropsWithRef<"div">;
  rootProps?: PassThroughPropsWithRef<"div">;
  loading?: boolean;
  enableStepping?: boolean;
};

export type YearlessDateInputRef = HTMLInputElement & {
  setValue: (value: YearlessDate | null) => {
    value: YearlessDate | null;
    isInputValid: boolean;
    isInputEmpty: boolean;
  };
  removePlaceholder: (value: string) => string;
};

/**
 * A masked date input component that allows for the input of a date in a specific format.
 *
 * Provides a `setDate` method that can be used to set the date from outside the component (e.g from a calendar).
 *
 * @internal This component is not intended to be used directly in consumer code.
 *
 * @param props - The props for the MaskedDateInput component.
 * @param props.onChange - The function to call when the date changes.
 * @param props.mode - The mode of the date input.
 */
export const YearlessDateInput = forwardRef<
  YearlessDateInputRef,
  YearlessDateInputProps
>(
  (
    {
      onChange,
      mode = "mm/dd",
      value,
      error,
      size,
      wrapperProps,
      rootProps,
      loading = false,
      enableStepping = false,
      className: classNameProp,
      ...restProps
    },
    ref,
  ) => {
    const placeholder = "__/__";
    const [inputValue, setInputValue] = useState(placeholder);
    const { options, removePlaceholder, transform } =
      yearlessDatePlaceholderMask({
        mode,
        placeholder,
        enableStepping,
      });
    const maskedInputRef = useMaskito({ options });
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = useMergeRefs([maskedInputRef, inputRef, ref]);
    const previousValue = usePrevious(value);
    const previousMode = usePrevious(mode);

    // Update the input value when the mode changes.
    useEffect(() => {
      if (mode !== previousMode) {
        setInputValue((oldInputValue) =>
          swapMode(oldInputValue, previousMode ?? mode, mode),
        );
      }
    }, [mode, previousMode]);

    const triggerAutoChange = useCallback(
      (stringValue: string) => {
        const {
          value: v,
          isInputValid,
          isInputEmpty,
        } = parseInputValue(stringValue, mode, removePlaceholder);
        onChange?.({
          event: null,
          value: v,
          isInputValid,
          isInputEmpty,
        });
      },
      [mode, removePlaceholder, onChange],
    );

    // Update the input value when the parent's value changes.
    useEffect(() => {
      if (
        previousValue?.day !== value?.day ||
        previousValue?.month !== value?.month
      ) {
        if (value?.day && value?.month) {
          const stringValue = stringifyYearlessDate(
            value.day,
            value.month,
            mode,
          );
          const transformedValue = transform(stringValue);
          setInputValue(transformedValue);
          if (transformedValue !== stringValue) {
            triggerAutoChange(transformedValue);
          }
        } else if (
          previousValue !== null &&
          previousValue !== undefined &&
          (value === null || (value?.day === null && value?.month === null))
        ) {
          setInputValue(placeholder);
        } else {
          return;
        }
      }
    }, [value, mode, previousValue, transform, triggerAutoChange]);

    const currentParsedData = useMemo(() => {
      return parseInputValue(inputValue, mode, removePlaceholder);
    }, [inputValue, mode, removePlaceholder]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      const {
        value: v,
        isInputValid,
        isInputEmpty,
      } = parseInputValue(event.target.value, mode, removePlaceholder);

      const isDateDifferent =
        (v === null) !== (currentParsedData.value === null) ||
        v?.day !== currentParsedData.value?.day ||
        v?.month !== currentParsedData.value?.month;
      if (!isDateDifferent) {
        return;
      }

      onChange?.({
        event,
        value: v ?? currentParsedData.value ?? null,
        isInputValid,
        isInputEmpty,
      });
    };

    // Extend the input ref with additional methods.
    // This provides a way to set the input value from outside the component.
    useImperativeHandle(ref, () => {
      const input = inputRef.current;
      if (!input) return null as unknown as YearlessDateInputRef;
      return Object.assign(input, {
        setValue: (value: YearlessDate | null) => {
          if (!value) {
            setInputValue(placeholder);
            return parseInputValue(placeholder, mode, removePlaceholder);
          }
          const valueAsString = stringifyYearlessDate(
            value.day ?? null,
            value.month ?? null,
            mode,
          );
          const transformedValue = transform(valueAsString);
          setInputValue(transformedValue);
          return parseInputValue(transformedValue, mode, removePlaceholder);
        },
        removePlaceholder,
      });
    }, [mode, placeholder, removePlaceholder, transform]);

    return (
      <div
        className={cx(textFieldStyles["textfield"])}
        data-anv="yearless-date-input"
        {...rootProps}
      >
        <div
          {...wrapperProps}
          className={cx(
            textFieldStyles["input-wrapper"],
            wrapperProps?.className,
            {
              [textFieldStyles["small"]]: size === "small",
              [textFieldStyles["large"]]: size === "large",
              [dateFieldYearlessStyles["small"]]: size === "small",
              [dateFieldYearlessStyles["large"]]: size === "large",
            },
          )}
        >
          <input
            {...restProps}
            ref={combinedRef}
            data-month-value={value?.month}
            data-day-value={value?.day}
            data-input-valid={currentParsedData.isInputValid}
            data-input-empty={currentParsedData.isInputEmpty}
            value={inputValue}
            onChange={handleChange}
            className={cx(
              textFieldStyles["input"],
              dateFieldYearlessStyles["input"],
              {
                [textFieldStyles["error"]]: error ?? false,
              },
              classNameProp,
            )}
          />
          {loading ? (
            <div className={textFieldStyles["loading-spinner-wrapper"]}>
              <Spinner />
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

YearlessDateInput.displayName = "YearlessDateInput";
